import React, { useEffect, useState } from "react";
import { Camera, ChevronLeft, ChevronRight, ImageOff, Loader2, Minus, Plus, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API } from "../lib/api";

export const photoUrl = (p) => {
  const token = localStorage.getItem("auth_token");
  return `${API}/completion-photos/${p.storage_path}${token ? `?auth=${token}` : ""}`;
};

export const photoWhen = (s) => (s ? new Date(s).toLocaleString([], {
  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

/**
 * One gallery for both photo types — installation photos read from the completion record, material
 * photos read from the location record. The caller passes ONE set, so the two types can never end
 * up in the same gallery. Newest first, full size with zoom, paging, and who / when / what on every
 * shot. Viewing or downloading changes nothing.
 */
export default function PhotoGallery({
  open, onClose, title, subtitle, photos = [], loading = false, onCapture, onDelete,
  metaOf = () => "", emptyText = "No photos yet", testid = "photo-gallery",
}) {
  const [idx, setIdx] = useState(null);         // open full-size viewer
  const [zoom, setZoom] = useState(1);

  useEffect(() => { setZoom(1); }, [idx]);
  useEffect(() => {
    if (idx === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => Math.min(photos.length - 1, (i ?? 0) + 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, (i ?? 0) - 1));
      if (e.key === "Escape") setIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, photos.length]);

  const current = idx === null ? null : photos[idx];

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Camera className="w-4 h-4 text-blue-700" /> {title}
          </DialogTitle>
          {subtitle && <div className="text-[11px] text-gray-600" data-testid={`${testid}-subtitle`}>{subtitle}</div>}
        </DialogHeader>

        {onCapture && (
          <Button className="h-11 w-full bg-blue-600 hover:bg-blue-700" onClick={onCapture}
                  data-testid={`${testid}-capture`}>
            <Camera className="w-4 h-4 mr-2" /> Take photos
          </Button>
        )}

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : photos.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500 flex flex-col items-center gap-1"
               data-testid={`${testid}-empty`}>
            <ImageOff className="w-5 h-5 text-gray-400" /> {emptyText}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testid}-grid`}>
            {photos.map((p, i) => (
              <div key={p.id} className="relative" data-testid={`${testid}-item-${p.id}`}>
                <button type="button" onClick={() => setIdx(i)} className="block w-full"
                        data-testid={`${testid}-open-${p.id}`}>
                  <img src={photoUrl(p)} alt={p.filename || "photo"}
                       className="w-full h-28 object-cover rounded border border-gray-200" />
                </button>
                <div className="text-[10px] text-gray-600 mt-0.5 leading-tight">
                  <div className="truncate">{p.uploader || "—"}</div>
                  <div className="truncate">{photoWhen(p.uploaded_at)}</div>
                  <div className="truncate text-gray-500">{metaOf(p)}</div>
                </div>
                {onDelete && (
                  <button type="button" onClick={() => onDelete(p)} title="Delete this photo"
                          data-testid={`${testid}-delete-${p.id}`}
                          className="absolute top-1 right-1 bg-white/90 border border-gray-300 rounded-full p-1">
                    <Trash2 className="w-3 h-3 text-red-700" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {current && (
          <Dialog open onOpenChange={(v) => { if (!v) setIdx(null); }}>
            <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] sm:max-w-5xl sm:w-[92vw] sm:h-[90vh] p-0 gap-0 bg-black border-0 overflow-hidden"
                           data-testid={`${testid}-viewer`}>
              <div className="flex items-center justify-between px-3 py-2 text-white bg-black/90 text-[11px]">
                <DialogTitle className="sr-only">{title}</DialogTitle>
                <span className="truncate" data-testid={`${testid}-viewer-meta`}>
                  {current.uploader || "—"} · {photoWhen(current.uploaded_at)} · {metaOf(current)}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="text-white h-9" data-testid={`${testid}-zoom-out`}
                          onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}><Minus className="w-4 h-4" /></Button>
                  <span className="tabular-nums w-10 text-center" data-testid={`${testid}-zoom`}>{Math.round(zoom * 100)}%</span>
                  <Button size="sm" variant="ghost" className="text-white h-9" data-testid={`${testid}-zoom-in`}
                          onClick={() => setZoom((z) => Math.min(5, +(z + 0.5).toFixed(1)))}><Plus className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-white h-9" onClick={() => setIdx(null)}
                          data-testid={`${testid}-viewer-close`}><X className="w-4 h-4" /></Button>
                </span>
              </div>
              <div className="relative flex-1 h-[calc(100%-88px)] overflow-auto bg-black touch-pan-x touch-pan-y">
                <img src={photoUrl(current)} alt={current.filename || "photo"}
                     onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2))}
                     style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                     className="mx-auto max-w-full max-h-full object-contain transition-transform"
                     data-testid={`${testid}-viewer-image`} />
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-black/90 text-white">
                <Button variant="ghost" className="text-white h-11" disabled={idx === 0}
                        onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid={`${testid}-prev`}>
                  <ChevronLeft className="w-5 h-5 mr-1" /> Newer
                </Button>
                <span className="text-[11px] tabular-nums" data-testid={`${testid}-position`}>{idx + 1} of {photos.length}</span>
                <Button variant="ghost" className="text-white h-11" disabled={idx >= photos.length - 1}
                        onClick={() => setIdx((i) => Math.min(photos.length - 1, i + 1))} data-testid={`${testid}-next`}>
                  Older <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
